import { Effect, Data } from "effect"
import { BlobToExtension } from "./mimes"
import { Base64Effect } from "@utils/utilities/effect/data-conversion"
import { isNative } from "@utils/underhood/browser"
import { IS_WEBVIEW } from "@utils/underhood/is-webview"
import type { FriendlyError } from "@utils/utilities/friendly-error"

/**
 * Constant that says whether the current device can download or not
 * It's basically just whether it's native, since the webview can't
 * download.
 */
export const CAN_DOWNLOAD = !isNative && !IS_WEBVIEW

/**
 * Do not include extension in filename. This may not work on all platforms.
 * @param blob - The blob to download
 * @param filename - The filename to download
 */
export const DownloadBlob = Effect.fn("DownloadBlob")(function* (blob: Blob, filename: string) {
    if (!CAN_DOWNLOAD)
        yield* Effect.fail(
            new DownloadNotAllowed({ message: "Downloading not allowed on this device" }),
        )
    const base64 = yield* Base64Effect(blob)
    const extension = yield* BlobToExtension(blob)
    downloadPath(base64, `${filename}.${extension}`)
    return yield* Effect.void
})

/**
 * Download a file from a blob path
 * @param blobPath - The blob path to download
 * @param filename - The filename to download
 */
export const downloadPath = (blobPath: string, filename: string) => {
    const link = document.createElement("a")
    link.href = blobPath
    link.download = filename
    link.target = "_blank"
    link.click()
}

export class DownloadNotAllowed
    extends Data.TaggedError("ceapp/DownloadNotAllowed")<{
        message: string
    }>
    implements FriendlyError
{
    get friendly() {
        return "Downloads are not enabled on this device!"
    }
}
